import { useTheme } from 'styled-components'

import { Box, Button, Container, Flex, Link, Typography } from '@fe-toolkit/web-components/ssr'

import { BRAND } from '@/components/pages/Home/home.constants'
import { useRuntimeConfig } from '@/config/runtimeConfig'

import { getCampaignPromoContent } from './campaignPromoConfig'

import type { TSxProp } from '@fe-toolkit/web-components/ssr'
import type { CampaignPromoContent } from './campaignPromoConfig'

/**
 * BrandButton (the atom every other section's flat black CTA uses) always applies its
 * own `sx` last, discarding any custom `sx` passed in — see its own docstring — so its
 * fixed `isSmall` padding (24px each side) can't be tightened from here. That padding
 * measured against the mobile frame leaves the headline too little room to stay on one
 * line at 390px (only ~176px available against the ~218px "Unlimited data. £12 a
 * month." actually renders at), which pushed it onto a second line and threw off the
 * whole bar's alignment. Pixel-sampling reference-imgs/mobile/mobile-1.png put the real
 * button's horizontal padding much closer to `theme.spacing.fine` (4px) than
 * BrandButton's 24px, so this CTA talks to the toolkit's own `Button` and re-applies
 * BrandButton's "solid" tone locally with that tighter padding instead.
 */
const ctaSx: TSxProp = ({ theme }) => ({
  backgroundColor: theme.colors.black,
  borderColor: theme.colors.black,
  borderRadius: theme.borderRadius.none,
  boxShadow: 'none',
  color: theme.colors.white,
  paddingLeft: theme.spacing.fine,
  paddingRight: theme.spacing.fine,
  '&:hover, &:active': { boxShadow: 'none', translate: 'none' },
  '&:focus': { translate: 'none' },
})

export type CampaignPromoBarProps = {
  /** Overrides the runtime-config-resolved content. Mainly for Storybook. */
  content?: CampaignPromoContent
}

/**
 * Green strip at the very top of the page. Reference: the top of
 * reference-imgs/desktop/desktop-1.png and reference-imgs/mobile/mobile-1.png.
 *
 * Config-driven rather than a hardcoded fixture: see campaignPromoConfig.ts and the
 * README's "Campaign Promo Bar" section for how marketing changes the copy, terms link
 * and CTA destination per environment with no rebuild.
 *
 * Mounted from src/routes/layout.tsx, above `<CommonHeader>`, not from the page component
 * — that is where the frames put it, and the federated header is rendered by the layout
 * before the page's own route tree, so nothing page-owned can land above it. The layout
 * gates it on the route (showsCampaignPromo there) so campaign copy stays off routes
 * that aren't campaign landings. A promo slot on CommonHeader itself would be tidier and
 * is still the follow-up with the fe_common owners, but it doesn't exist yet — check
 * @mf-types/ after a build, CommonHeader is only typed as
 * ComponentType<Record<string, unknown>> locally.
 *
 * Being outside the page's `<Container>` is what lets the green bleed to both viewport
 * edges; nested inside it, the bar was clamped to the page's middle grid column and the
 * background stopped short of the gutters above `medium`.
 *
 * The button bleeding into the top-right viewport corner in the desktop frame is skipped
 * — a fidelity detail that fights the Container gutter.
 */
const CampaignPromoBar = ({ content }: CampaignPromoBarProps) => {
  const theme = useTheme()
  const runtimeConfig = useRuntimeConfig()
  const { headline, termsLabel, termsHref, ctaLabel, ctaHref } = content ?? getCampaignPromoContent(runtimeConfig)

  const upMedium = theme.breakpoints.up('medium')

  // The two frames disagree on which side "Terms apply" sits: grouped with the headline
  // on the left in mobile-1.png, but grouped with the CTA on the right in desktop-1.png.
  // That's a genuine change of neighbour, not just a wrap — so it's rendered twice (mobile
  // and desktop copies, each toggled with the other via `display: none`) rather than
  // reflowed with a single flexWrap, which can only ever reorder items, not re-pair them.
  // Same trick as AppPromoSection's Download-button/QR-tile swap.
  const renderTermsLink = (key: 'mobile' | 'desktop') => (
    <Link key={key} chevron={false} href={termsHref} variant="murmur">
      <Typography as="span" color="black" variant="murmur">
        {termsLabel}
      </Typography>
    </Link>
  )

  return (
    <Container as="section" py="tiny" sx={{ backgroundColor: BRAND.promoGreen }}>
      <Flex alignItems="center" justifyContent="space-between" sx={{ gap: theme.spacing.tiny }}>
        {/* No `flexWrap` here — the CTA has to stay on the right on one row at every
            width the frames cover, so it's the headline that gives way (wrapping onto
            a second line of its own) rather than the whole row breaking. `tiny` (not
            `small`) between the two groups leaves the headline enough room to actually
            stay on that one line at 390px, matching the mobile frame. */}
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Typography as="p" color="black" variant="murmurBold">
            {headline}
          </Typography>

          <Box sx={{ [upMedium]: { display: 'none' } }}>{renderTermsLink('mobile')}</Box>
        </Box>

        <Flex alignItems="center" sx={{ flex: '0 0 auto', gap: theme.spacing.small }}>
          <Box sx={{ display: 'none', [upMedium]: { display: 'block' } }}>{renderTermsLink('desktop')}</Box>

          <Button isSmall href={ctaHref} sx={ctaSx} variant="standard">
            {ctaLabel}
          </Button>
        </Flex>
      </Flex>
    </Container>
  )
}

export default CampaignPromoBar
export type { CampaignPromoContent }
